import { useNode, useEditor } from "@craftjs/core";
import React from "react";
import { Box, IconButton, TextField as MuiTextField } from "@mui/material";
import { useInlineEdit } from "../../hooks/useInlineEdit";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { SliderField, SettingsSection } from "../ui/form/SettingsField";

export interface NavMenuItem {
  label: string;
  href: string;
}

export interface NavMenuProps {
  items?: NavMenuItem[];
  gap?: number;
  fontSize?: number;
  fontWeight?: number;
}

export const NavMenu = ({
  items = [],
  gap = 24,
  fontSize = 14,
  fontWeight = 500,
}: NavMenuProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { enabled: editorEnabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const { getEditableProps } = useInlineEdit(setProp);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (editorEnabled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Empty state for editor
  if (items.length === 0) {
    return (
      <Box
        ref={(ref: HTMLDivElement | null) => {
          if (ref) connect(drag(ref));
        }}
        sx={{
          minWidth: "120px",
          minHeight: "36px",
          border: "2px dashed #94a3b8",
          borderRadius: "6px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f1f5f9",
          ...(selected && { ...selectedOutlineSx, outlineOffset: "2px" }),
        }}
      >
        <Box sx={{ fontSize: "12px", color: "#64748b" }}>Menu Items</Box>
      </Box>
    );
  }

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: `${gap}px`,
        flexShrink: 0,
        ...(selected && { ...selectedOutlineSx, outlineOffset: "2px" }),
      }}
    >
      {items.map((item, index) => (
        <Box
          key={index}
          component="a"
          href={item.href}
          onClick={handleClick}
          {...getEditableProps(`menuItem-${index}`, item.label, (v) => {
            setProp((p: NavMenuProps) => {
              if (p.items && p.items[index]) { p.items[index].label = v; }
            });
          })}
          sx={{
            fontWeight: fontWeight,
            fontSize: `${fontSize}px`,
            color: "inherit",
            textDecoration: "none",
            transition: "opacity 0.2s",
            whiteSpace: "nowrap",
            "&:hover": { opacity: 0.7 },
          }}
        >
          {item.label}
        </Box>
      ))}
    </Box>
  );
};

export const NavMenuSettings = () => {
  const {
    actions: { setProp },
    items,
    gap,
    fontSize,
    fontWeight,
  } = useNode((node) => ({
    items: node.data.props.items || [],
    gap: node.data.props.gap,
    fontSize: node.data.props.fontSize,
    fontWeight: node.data.props.fontWeight,
  }));

  const addItem = () => {
    setProp((props: NavMenuProps) => {
      props.items = [...(props.items || []), { label: "Link", href: "/" }];
    });
  };

  const removeItem = (index: number) => {
    setProp((props: NavMenuProps) => {
      props.items = (props.items || []).filter((_, i) => i !== index);
    });
  };

  const updateItem = (index: number, field: keyof NavMenuItem, value: string) => {
    setProp((props: NavMenuProps) => {
      if (props.items && props.items[index]) {
        props.items[index][field] = value;
      }
    });
  };

  const moveItem = (index: number, direction: "up" | "down") => {
    setProp((props: NavMenuProps) => {
      if (!props.items) return;
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= props.items.length) return;
      const newItems = [...props.items];
      [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
      props.items = newItems;
    });
  };

  return (
    <SettingsSection>
      {/* Menu Items */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151" }}>Menu Items</Box>
          <IconButton size="small" onClick={addItem} sx={{ bgcolor: "#e0f2fe", "&:hover": { bgcolor: "#bae6fd" } }}>
            <Box sx={{ fontSize: "16px", lineHeight: 1 }}>+</Box>
          </IconButton>
        </Box>

        {items.map((item: NavMenuItem, index: number) => (
          <Box
            key={index}
            sx={{
              p: 1.5,
              mb: 1,
              bgcolor: "#f9fafb",
              borderRadius: 1,
              border: "1px solid #e5e7eb",
            }}
          >
            {/* Item header with reorder and delete */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => moveItem(index, "up")}
                  disabled={index === 0}
                  sx={{ p: 0.5, fontSize: "12px" }}
                >
                  ↑
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => moveItem(index, "down")}
                  disabled={index === items.length - 1}
                  sx={{ p: 0.5, fontSize: "12px" }}
                >
                  ↓
                </IconButton>
              </Box>
              <IconButton
                size="small"
                onClick={() => removeItem(index)}
                sx={{ p: 0.5, color: "#ef4444", "&:hover": { bgcolor: "#fee2e2" } }}
              >
                <Box sx={{ fontSize: "14px", lineHeight: 1 }}>×</Box>
              </IconButton>
            </Box>

            {/* Label input */}
            <MuiTextField
              fullWidth
              size="small"
              label="Label"
              value={item.label}
              onChange={(e) => updateItem(index, "label", e.target.value)}
              sx={{ mb: 1 }}
            />

            {/* Href input */}
            <MuiTextField
              fullWidth
              size="small"
              label="URL"
              value={item.href}
              onChange={(e) => updateItem(index, "href", e.target.value)}
            />
          </Box>
        ))}

        {items.length === 0 && (
          <Box sx={{ p: 2, textAlign: "center", color: "#9ca3af", fontSize: "13px" }}>
            ยังไม่มี menu items - กด + เพื่อเพิ่ม
          </Box>
        )}
      </Box>

      {/* Style settings */}
      <SliderField
        label="Gap"
        value={gap}
        onChange={(v) => setProp((props: NavMenuProps) => (props.gap = v))}
        min={8}
        max={48}
        unit="px"
      />

      <SliderField
        label="Font Size"
        value={fontSize}
        onChange={(v) => setProp((props: NavMenuProps) => (props.fontSize = v))}
        min={12}
        max={20}
        unit="px"
      />

      <SliderField
        label="Font Weight"
        value={fontWeight}
        onChange={(v) => setProp((props: NavMenuProps) => (props.fontWeight = v))}
        min={400}
        max={700}
        step={100}
      />
    </SettingsSection>
  );
};

NavMenu.craft = {
  displayName: "Nav Menu",
  props: {
    items: [
      { label: "Home", href: "/" },
      { label: "About", href: "/about" },
      { label: "Contact", href: "/contact" },
    ],
    gap: 24,
    fontSize: 14,
    fontWeight: 500,
  },
  rules: {
    canDrag: () => true,
  },
  related: {
    settings: NavMenuSettings,
  },
};
